// import { Link } from 'gatsby';
import React from 'react';

interface PaginationProps {
  currentPage: number;
  numberOfPosts: number;
  className?: string;
  postsPerPage: number;
  currentPath: string;
  onPageChange: (page: number) => void;
}

export default function Pagination({
  currentPage,
  numberOfPosts,
  className,
  onPageChange,
  postsPerPage,
  currentPath
}: PaginationProps) {
  const range = 1;
  const numPages = Math.ceil(numberOfPosts / postsPerPage);
  const createPageArray = () => {
    const pages = new Set<number>();

    // Always include the first page
    pages.add(1);

    // Include pages based on the current page position
    if (currentPage === 1) {
      for (let i = 2; i <= Math.min(3, numPages); i++) {
        pages.add(i);
      }
    } else if (currentPage === numPages) {
      for (let i = Math.max(numPages - 2, 1); i < numPages; i++) {
        pages.add(i);
      }
    } else {
      const startPage = Math.max(currentPage - range, 2);
      const endPage = Math.min(currentPage + range, numPages - 1);
      for (let i = startPage; i <= endPage; i++) {
        pages.add(i);
      }
    }

    // Always include the last page if it's not already included
    if (numPages > 1) {
      pages.add(numPages);
    }

    return Array.from(pages).sort((a, b) => a - b);
  };

  const pages = createPageArray();

  const renderPageLink = (page: number) => {
    if (currentPage === page) {
      return <span aria-current="page">{page}</span>;
    }
    // REPLACE WITH GATSBY LINK
    return (
      <a
        href={`${currentPath}?page=${page}#latest-posts`}
        aria-label={`Go to page ${page}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.preventDefault();
          e.nativeEvent.stopImmediatePropagation(); // Prevent the default navigation behavior
          onPageChange(page); // Call the onPageChange function
        }}>
        {page}
      </a>
    );
  };

  return (
    <nav aria-label="Pagination Navigation">
      <ul
        className={`list-unstyled d-flex justify-content-center pagination mb-0 ${className || ''}`}>
        {currentPage > 1 && (
          <li>
            <a
              href={`${currentPath}?page=${currentPage - 1}#latest-posts`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.preventDefault();
                e.nativeEvent.stopImmediatePropagation(); // Prevent the default navigation behavior
                onPageChange(currentPage - 1); // Call the onPageChange function
              }}
              className="ecs-font-icon-nav-angle-left font-icon-size-md"
              aria-label="Previous page"
            />
          </li>
        )}
        {pages.map((pageNumber, index) => {
          const previousPage = pages[index - 1];
          return (
            <React.Fragment key={pageNumber}>
              {/* Insert ellipsis if there is a gap between the current page and the previous page */}
              {previousPage && pageNumber !== previousPage + 1 && (
                <li className="d-flex justify-content-center align-items-center">
                  <span className="lh-1">&hellip;</span>
                </li>
              )}

              <li
                className={
                  currentPage === pageNumber
                    ? 'rounded-circle bg-midnight-blue text-white d-flex justify-content-center align-items-center flex-shrink-0'
                    : ''
                }>
                {renderPageLink(pageNumber)}
              </li>
            </React.Fragment>
          );
        })}
        {currentPage < numPages && (
          <li>
            <a
              href={`${currentPath}?page=${currentPage + 1}#latest-posts`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.preventDefault();
                e.nativeEvent.stopImmediatePropagation();
                // Prevent the default navigation behavior
                onPageChange(currentPage + 1); // Call the onPageChange function
              }}
              className="ecs-font-icon-nav-angle-right font-icon-size-md"
              aria-label="Next page"
            />
          </li>
        )}
      </ul>
    </nav>
  );
}
